import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../utils/auth.jsx';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const ModelsContext = createContext(null);

// Derive MODEL_GROUPS from flat models map: { "OpenAI": ["gpt-5.4", ...], ... }
function buildModelGroups(models) {
  const groups = {};
  for (const [key, info] of Object.entries(models)) {
    const group = info.group || info.provider || 'Other';
    if (!groups[group]) groups[group] = [];
    groups[group].push(key);
  }
  return groups;
}

export function ModelsProvider({ children }) {
  const { token } = useAuth();
  const [models, setModels] = useState({});
  const [deprecatedMap, setDeprecatedMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/models`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) {
            setModels(data.models || {});
            setDeprecatedMap(data.deprecated_map || {});
          }
        }
      } catch (e) {
        console.error('Failed to fetch models:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const modelGroups = useMemo(() => buildModelGroups(models), [models]);

  // Resolve a model key — returns the active replacement if deprecated, or the key itself
  const resolveModel = useCallback((key) => {
    if (!key) return key;
    return deprecatedMap[key] || key;
  }, [deprecatedMap]);

  // Resolve an array of model keys, removing duplicates after resolution
  const resolveModelList = useCallback((keys) => {
    if (!keys || keys.length === 0) return keys;
    const resolved = keys.map(k => deprecatedMap[k] || k);
    return [...new Set(resolved)];
  }, [deprecatedMap]);

  const value = useMemo(() => ({
    models,
    modelGroups,
    deprecatedMap,
    resolveModel,
    resolveModelList,
    modelsLoading: loading,
  }), [models, modelGroups, deprecatedMap, resolveModel, resolveModelList, loading]);

  return <ModelsContext.Provider value={value}>{children}</ModelsContext.Provider>;
}

export function useModels() {
  const ctx = useContext(ModelsContext);
  if (!ctx) throw new Error('useModels must be used within ModelsProvider');
  return ctx;
}
